import { Link } from "react-router-dom";
import { SEO } from "@/components/SEO";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { 
  MapPin, Search, CheckCircle, XCircle, ArrowRight, ChevronDown, Heart,
  MessageCircle, Eye, Clock, Users, Lightbulb, AlertTriangle
} from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";

const faqData = [
  {
    question: "Kann ich wirklich jemanden ohne Namen finden?",
    answer: "Ja! Mit Pinme brauchst du keinen Namen. Du nutzt Ort, Zeit und Merkmale als Suchkriterien. Wenn die Person denselben Ort besucht, findet sie deinen Pin."
  },
  {
    question: "Was, wenn ich nur wenig über die Person weiß?",
    answer: "Oft reichen Ort und ungefähre Zeit. Je mehr Details du hinzufügst (Kleidung, Aussehen, Situation), desto höher die Chance, dass sich die richtige Person meldet."
  },
  {
    question: "Wie lange sollte ich warten?",
    answer: "Gib deinem Pin Zeit. Bei regelmäßigen Orten (Pendlerstrecke, Supermarkt) kann es Tage dauern, bei einmaligen Events Wochen oder Monate."
  },
  {
    question: "Ist die Suche anonym?",
    answer: "Dein Pin ist anonym – deine E-Mail wird nicht öffentlich angezeigt. Wenn du auf einen Pin antwortest, wird deine E-Mail an den Pin-Ersteller weitergegeben."
  },
  {
    question: "Was kostet die Suche?",
    answer: "Pinme ist komplett kostenlos. Du zahlst nichts für das Setzen von Pins oder für Nachrichten."
  }
];

const PersonOhneNamenWiederfinden = () => {
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Person ohne Namen wiederfinden – So klappt es wirklich (Schritt-für-Schritt Guide)",
    "description": "Du hast jemanden gesehen, kennst aber den Namen nicht? So findest du eine Person wieder – mit Ort, Zeit, Erinnerung und Pinme.",
    "author": { "@type": "Organization", "name": "Pinme" },
    "publisher": { "@type": "Organization", "name": "Pinme", "logo": { "@type": "ImageObject", "url": "https://pinme.ch/logo-pinme-new.png" } },
    "datePublished": "2024-01-20",
    "dateModified": new Date().toISOString().split('T')[0]
  };

  return (
    <>
      <SEO
        title="Person ohne Namen wiederfinden – So klappt es wirklich | Pinme"
        description="Du hast jemanden gesehen, kennst aber den Namen nicht? So findest du eine Person wieder – mit Ort, Zeit, Erinnerung und Pinme. Funktioniert anonym und ohne Kontaktdaten."
        canonical="https://pinme.ch/blog/person-ohne-namen-wiederfinden"
        keywords="person ohne namen wiederfinden, jemanden wiederfinden ohne name, person suchen ohne daten, ich suche jemanden kenne nur aussehen, verpasste begegnung finden"
        hreflang={[
          { locale: "de-CH", url: "https://pinme.ch/blog/person-ohne-namen-wiederfinden" },
          { locale: "de-DE", url: "https://pinme.ch/blog/person-ohne-namen-wiederfinden" },
          { locale: "de-AT", url: "https://pinme.ch/blog/person-ohne-namen-wiederfinden" },
          { locale: "x-default", url: "https://pinme.ch/blog/person-ohne-namen-wiederfinden" }
        ]}
        structuredData={[faqSchema, articleSchema]}
      />

      <PageContainer className="py-8 md:py-12">
        <header className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-coral-100 text-coral-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Search className="h-4 w-4" />
            Vollständige Anleitung
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-pinme bg-clip-text text-transparent mb-6 leading-normal pb-1">
            Person ohne Namen wiederfinden – So klappt es wirklich
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            Du hast jemanden gesehen, der dich nicht mehr loslässt – aber du kennst weder Namen noch Kontaktdaten. 
            Hier erfährst du Schritt für Schritt, wie du diese Person trotzdem wiederfinden kannst.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-gradient-to-r from-coral-500 to-coral-600 hover:from-coral-600 hover:to-coral-700">
              <Link to="/karte">
                <MapPin className="mr-2 h-5 w-5" />
                Jetzt Person suchen
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/pinnwand">
                <Eye className="mr-2 h-5 w-5" />
                Werde ich gesucht?
              </Link>
            </Button>
          </div>
        </header>

        <article className="prose prose-lg dark:prose-invert max-w-4xl mx-auto">
          
          {/* Warum Menschen suchen */}
          <section className="mb-12">
            <h2 className="text-2xl md:text-3xl font-bold bg-gradient-pinme bg-clip-text text-transparent mb-6">
              Warum Menschen jemanden ohne Namen suchen
            </h2>
            <p>
              Es passiert täglich: Ein kurzer Blickkontakt im Zug, ein Lächeln an der Supermarktkasse, 
              ein intensives Gespräch auf einem Festival – und dann ist die Person weg. <strong>Du weißt 
              nicht, wie sie heißt, hast keine Nummer, kein Social-Media-Profil.</strong>
            </p>
            <p>
              Diese Situation nennt man eine <strong>verpasste Begegnung</strong> (englisch: "Missed Connection"). 
              Millionen Menschen erleben sie jedes Jahr und fragen sich danach: <em>Wie finde ich jemanden wieder, 
              dessen Namen ich nicht kenne?</em>
            </p>
            <div className="bg-muted/50 p-6 rounded-xl my-6">
              <p className="font-medium mb-2">Typische Situationen:</p>
              <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                <li>Blickkontakt im öffentlichen Verkehr (Zug, Tram, Bus)</li>
                <li>Kurzes Gespräch auf einer Party oder einem Event</li>
                <li>Begegnung im Alltag (Supermarkt, Café, Fitnessstudio)</li>
                <li>Jemand, den du regelmäßig siehst, aber nie ansprichst</li>
              </ul>
            </div>
          </section>

          {/* Was man noch weiß */}
          <section className="mb-12">
            <h2 className="text-2xl md:text-3xl font-bold bg-gradient-pinme bg-clip-text text-transparent mb-6">
              Was du wahrscheinlich noch weißt
            </h2>
            <p>
              Auch ohne Namen hast du oft mehr Informationen als du denkst. Diese Details sind der Schlüssel 
              zum Wiederfinden:
            </p>
            <div className="grid md:grid-cols-2 gap-4 my-6">
              {[
                { icon: MapPin, title: "Der Ort", desc: "Wo genau war die Begegnung? Welche Haltestelle, welches Geschäft, welches Festival?" },
                { icon: Clock, title: "Die Zeit", desc: "Wann war es? Welcher Tag, welche Uhrzeit? Bei Pendlern: War es morgens oder abends?" },
                { icon: Eye, title: "Das Aussehen", desc: "Haarfarbe, Kleidung, besondere Merkmale, geschätztes Alter" },
                { icon: MessageCircle, title: "Die Situation", desc: "Was ist passiert? Blickkontakt, Gespräch, gemeinsames Erlebnis?" },
              ].map((item, i) => (
                <div key={i} className="p-4 bg-card border rounded-xl">
                  <div className="flex items-center gap-3 mb-2">
                    <item.icon className="h-5 w-5 text-coral-500" />
                    <h3 className="font-semibold">{item.title}</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Was nicht funktioniert */}
          <section className="mb-12">
            <h2 className="text-2xl md:text-3xl font-bold bg-gradient-pinme bg-clip-text text-transparent mb-6">
              Warum Google, Social Media & Foren scheitern
            </h2>
            <p>
              Viele versuchen zuerst, die Person über klassische Wege zu finden. Das funktioniert fast nie:
            </p>
            <div className="space-y-3 my-6">
              {[
                { title: "Google-Suche", desc: "Ohne Namen findest du nichts. Google braucht konkrete Suchbegriffe." },
                { title: "Facebook-Gruppen", desc: "Dein Post geht unter zwischen Hunderten anderen. Keine Garantie, dass die Person in der Gruppe ist." },
                { title: "Reddit & Foren", desc: "Zu langsam, zu unspezifisch, keine lokale Eingrenzung möglich." },
                { title: "Instagram/TikTok", desc: "Ohne Namen oder Profil-Link keine Chance, die Person zu finden." },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 p-4 bg-destructive/5 border border-destructive/20 rounded-lg">
                  <XCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">{item.title}</p>
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <p>
              <strong>Das Kernproblem:</strong> Diese Plattformen sind nicht für lokale, ortsbasierte Suchen gemacht. 
              Sie verstehen nicht, dass du jemanden an einem bestimmten Ort zu einer bestimmten Zeit gesucht hast.
            </p>
          </section>

          {/* Der beste Ansatz */}
          <section className="mb-12">
            <h2 className="text-2xl md:text-3xl font-bold bg-gradient-pinme bg-clip-text text-transparent mb-6">
              Der beste Ansatz: Ortsbasierte Suche mit Pinme
            </h2>
            <p>
              Die effektivste Methode, eine Person ohne Namen wiederzufinden, ist eine <strong>ortsbasierte Suche</strong>. 
              Warum? Weil der Ort das einzige ist, was euch verbindet – und Menschen kehren oft an dieselben Orte zurück.
            </p>
            <div className="bg-gradient-to-br from-coral-50 to-background p-6 rounded-xl border my-6">
              <h3 className="font-semibold text-xl mb-4">So funktioniert Pinme:</h3>
              <ol className="space-y-3">
                {[
                  "Du setzt einen Pin genau dort, wo die Begegnung stattfand",
                  "Du beschreibst die Person, die Zeit und was passiert ist",
                  "Dein Pin ist anonym – niemand sieht deine Daten",
                  "Wenn die gesuchte Person den Ort besucht, sieht sie deinen Pin",
                  "Sie kann antworten – erst dann werden Kontakte ausgetauscht"
                ].map((step, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-coral-500 text-white flex items-center justify-center font-bold text-sm">
                      {i + 1}
                    </span>
                    <span className="pt-0.5">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          </section>

          {/* Beispiel-Szenarien */}
          <section className="mb-12">
            <h2 className="text-2xl md:text-3xl font-bold bg-gradient-pinme bg-clip-text text-transparent mb-6">
              Beispiel-Szenarien: So schreibst du deinen Pin
            </h2>
            <div className="space-y-4">
              <div className="bg-muted/30 p-5 rounded-xl border-l-4 border-coral-500">
                <p className="font-medium mb-2">Beispiel 1: Zugbegegnung</p>
                <p className="italic text-muted-foreground text-sm">
                  "Freitag, 17.45 Uhr, S8 von Zürich nach Winterthur. Du saßt mir gegenüber – dunkle Haare, 
                  blauer Pullover, hast ein Buch gelesen. Wir haben uns mehrmals angelächelt. Ich hätte dich 
                  gerne angesprochen. Falls du das liest – meld dich! 😊"
                </p>
              </div>
              <div className="bg-muted/30 p-5 rounded-xl border-l-4 border-coral-500">
                <p className="font-medium mb-2">Beispiel 2: Supermarkt</p>
                <p className="italic text-muted-foreground text-sm">
                  "Dienstagabend ca. 18 Uhr, Migros Bahnhofstrasse. Du standest vor mir an der Kasse – 
                  rote Jacke, freundliches Lächeln. Wir haben kurz geredet, dann warst du weg. Würde mich 
                  freuen, wenn du dich meldest."
                </p>
              </div>
              <div className="bg-muted/30 p-5 rounded-xl border-l-4 border-coral-500">
                <p className="font-medium mb-2">Beispiel 3: Festival</p>
                <p className="italic text-muted-foreground text-sm">
                  "Street Parade, Samstag beim Truck am Bellevue. Du hattest einen grünen Rucksack und 
                  wir haben zusammen getanzt. Das war magisch – ich würde dich so gerne wiedersehen!"
                </p>
              </div>
            </div>
          </section>

          {/* Häufige Fehler */}
          <section className="mb-12">
            <h2 className="text-2xl md:text-3xl font-bold bg-gradient-pinme bg-clip-text text-transparent mb-6">
              Häufige Fehler vermeiden
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-card border rounded-xl">
                <div className="flex items-center gap-2 mb-2 text-green-600">
                  <CheckCircle className="h-5 w-5" />
                  <span className="font-semibold">Richtig</span>
                </div>
                <ul className="text-sm space-y-2 text-muted-foreground">
                  <li>✓ Genauen Ort und Zeit angeben</li>
                  <li>✓ Konkrete Merkmale beschreiben</li>
                  <li>✓ Freundlich und respektvoll schreiben</li>
                  <li>✓ Geduldig sein und warten</li>
                </ul>
              </div>
              <div className="p-4 bg-card border rounded-xl">
                <div className="flex items-center gap-2 mb-2 text-destructive">
                  <XCircle className="h-5 w-5" />
                  <span className="font-semibold">Falsch</span>
                </div>
                <ul className="text-sm space-y-2 text-muted-foreground">
                  <li>✗ Zu vage bleiben ("irgendwo in Zürich")</li>
                  <li>✗ Aufdringlich oder creepy wirken</li>
                  <li>✗ Mehrere Pins für dieselbe Person</li>
                  <li>✗ Zu schnell aufgeben</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Schritt-für-Schritt */}
          <section className="mb-12">
            <h2 className="text-2xl md:text-3xl font-bold bg-gradient-pinme bg-clip-text text-transparent mb-6">
              Schritt-für-Schritt Anleitung
            </h2>
            <div className="space-y-4">
              {[
                { step: 1, title: "Erinnere dich an alle Details", desc: "Schreibe alles auf: Ort, Zeit, Aussehen, Kleidung, was passiert ist." },
                { step: 2, title: "Öffne Pinme", desc: "Gehe auf pinme.ch und öffne die Karte." },
                { step: 3, title: "Finde den genauen Ort", desc: "Zoome auf der Karte an den Ort der Begegnung." },
                { step: 4, title: "Setze einen Pin", desc: "Rechtsklick (Desktop) oder langes Drücken (Mobile) an der Stelle." },
                { step: 5, title: "Beschreibe die Begegnung", desc: "Fülle das Formular aus: Zeit, Beschreibung, was du sagen möchtest." },
                { step: 6, title: "Veröffentliche anonym", desc: "Dein Pin wird nach kurzer Prüfung freigeschaltet." },
                { step: 7, title: "Warte auf Antwort", desc: "Du bekommst eine E-Mail, wenn jemand antwortet." },
              ].map((item) => (
                <div key={item.step} className="flex items-start gap-4 p-4 bg-card border rounded-xl">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-coral-500 text-white flex items-center justify-center font-bold">
                    {item.step}
                  </span>
                  <div>
                    <h3 className="font-semibold">{item.title}</h3>
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Erfolgschancen */}
          <section className="mb-12">
            <h2 className="text-2xl md:text-3xl font-bold bg-gradient-pinme bg-clip-text text-transparent mb-6">
              Erfolgschancen realistisch einschätzen
            </h2>
            <p>
              Die Chancen, eine Person ohne Namen wiederzufinden, hängen von mehreren Faktoren ab:
            </p>
            <div className="grid gap-4 my-6">
              <div className="flex items-start gap-3 p-4 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <p className="font-medium text-green-800 dark:text-green-200">Hohe Erfolgswahrscheinlichkeit</p>
                  <p className="text-sm text-green-700 dark:text-green-300">Regelmäßige Orte (Pendlerstrecke, Stammcafé), detaillierte Beschreibung, zeitnah gepostet</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div>
                  <p className="font-medium text-yellow-800 dark:text-yellow-200">Mittlere Erfolgswahrscheinlichkeit</p>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">Einmalige Events (Festival, Konzert), gute Beschreibung, Person war lokal</p>
                </div>
              </div>
            </div>
            <p>
              <strong>Wichtig:</strong> Auch wenn die Chancen nicht 100% sind – ohne Versuch ist die Chance 0%. 
              Viele verpasste Begegnungen wurden durch Pinme wiedergefunden.
            </p>
          </section>

          {/* FAQ */}
          <section className="mb-12">
            <h2 className="text-2xl md:text-3xl font-bold bg-gradient-pinme bg-clip-text text-transparent mb-6">
              Häufige Fragen
            </h2>
            <div className="space-y-3">
              {faqData.map((item, index) => (
                <div key={index} className="border rounded-xl overflow-hidden">
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/50 transition-colors"
                  >
                    <span className="font-medium pr-4">{item.question}</span>
                    <ChevronDown className={cn("h-5 w-5 flex-shrink-0 transition-transform", expandedFaq === index && "rotate-180")} />
                  </button>
                  {expandedFaq === index && (
                    <div className="px-4 pb-4 text-muted-foreground">{item.answer}</div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="bg-gradient-to-br from-coral-50 via-background to-primary/5 p-8 rounded-2xl border text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Bereit, die Person wiederzufinden?
            </h2>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Du hast jemanden gesehen, der dich nicht loslässt. Jetzt hast du die Möglichkeit, 
              diese Person wiederzufinden – anonym, lokal und kostenlos.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-gradient-to-r from-coral-500 to-coral-600 hover:from-coral-600 hover:to-coral-700">
                <Link to="/karte">
                  <MapPin className="mr-2 h-5 w-5" />
                  Jetzt Pin setzen
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/pinnwand">Pinnwand durchsuchen</Link>
              </Button>
            </div>
          </section>
        </article>
      </PageContainer>
    </>
  );
};

export default PersonOhneNamenWiederfinden;
